import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import { ArrowLeft, ArrowRight, Mic, Square, Wand2 } from 'lucide-react'
import { Button } from 'components/ui'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import {
  ChatComposer,
  ChatComposerEditor,
  ChatComposerActions,
  ChatComposerActionGroup,
} from 'components/ui/ChatComposer'
import { ChatDictation } from 'components/ui/ChatComposer/Dictation'
import { ReferenceMediaSection } from 'components/TasksV2/TaskCreation/components/ReferenceMediaSection'
import { MediaUploadActions } from 'components/TasksV2/TaskCreation/components/MediaUploadActions'
import DraggableMediaGallery from 'components/TasksV2/UI/DraggableMediaGallery'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useAiPromptForm, IAIPromptStepProps } from '../../hooks/useAiPromptForm'
import { useAiCreationText } from '../../hooks/useAiCreationText'
import AIMessage from '../../AIMessage'
import styles from './styles.module.css'

export default function AIPromptStep(props: IAIPromptStepProps) {
  const form = useAiPromptForm(props)
  const [expanded, setExpanded] = useState(false)
  const t = useAiCreationText()
  const rtl = useSelector(isRtlSelector)
  const { sumbitStep } = useFormStepContext()
  const { speech } = form.dictation

  return (
    <div className={styles.prompt}>
      <AIMessage title={form.t_prompt_title} subtitle={form.t_prompt_subtitle} />
      <div className={styles.generator}>
        {!speech.listening && !form.isQuestion && form.isPromptGeneratorAvailable && (
          <Button
            type="button"
            variant="secondary"
            startIcon={<Wand2 size={16} />}
            isDisabled={form.isUploading}
            onClick={form.handleOpenPromptGenerator}
          >
            {form.t_prompt_generator}
          </Button>
        )}
      </div>
      <div className={styles.composerFrame}>
        <ChatComposer
          className={`${styles.composer} ${!speech.listening && speech.speechSupported ? styles.withVoice : ''}`}
        >
          <ChatDictation {...form.dictation} />
          <Controller name="prompt" control={form.control} as={<span hidden />} />
          <ChatComposerEditor
            id="ai-audit-prompt"
            input={form.input}
            label={form.t_prompt_title}
            placeholder={form.isQuestion ? form.t_prompt_qst : form.t_prompt_vm}
            value={form.promptValue}
            expanded={expanded}
            hidden={!!speech.listening}
            disabled={false}
            submitOnEnter={false}
            onChange={(value) => {
              speech.handleManualInput()
              form.setValue('prompt', value)
              form.setHasPromptError(false)
            }}
            onSend={() => {}}
            onCancel={form.dictation.cancel}
            onToggleExpanded={() => setExpanded((value) => !value)}
          />
          {!form.isQuestion && (
            <div className={styles.media}>
              <DropzoneWrapper
                onDrop={form.onDrop}
                onPaste={form.handlePaste}
                disabled={form.isUploading}
                role="group"
                aria-label={form.uploadText.dropImagesHint}
              >
                <ReferenceMediaSection>
                  <MediaUploadActions
                    disabled={form.isUploading}
                    onImage={() => form.imageInputRef.current?.click()}
                    onCapture={(file) => {
                      void form.uploadImages([file])
                    }}
                  />
                  <DraggableMediaGallery
                    layout="grid"
                    uploadingUrls={form.uploadingUrls}
                    attachedImages={form.referenceImages}
                    isShowRemoveIcon
                    isDragDisabled={form.isUploading}
                    onRemoveMedia={form.handleRemoveMedia}
                    onReorder={form.handleReorderMedia}
                  />
                </ReferenceMediaSection>
              </DropzoneWrapper>
            </div>
          )}
          <HiddenFileInput ref={form.imageInputRef} onChange={form.handleImageUpload} accept="image/*" multiple />
          <Controller name="reference_images" control={form.control} defaultValue={[]} as={<span hidden />} />
          {speech.listening && (
            <ChatComposerActions>
              <ChatComposerActionGroup>
                <Button type="button" variant="secondary" onClick={form.dictation.cancel}>
                  {t.cancel}
                </Button>
                <Button
                  type="button"
                  startIcon={<Square size={12} fill="currentColor" />}
                  onClick={speech.stopListening}
                >
                  {t.stopInsert}
                </Button>
              </ChatComposerActionGroup>
            </ChatComposerActions>
          )}
        </ChatComposer>
        {!speech.listening && speech.speechSupported && (
          <div className={styles.voice}>
            <Button
              type="button"
              variant="icon"
              w="96px"
              minW="96px"
              h="96px"
              bg="#222222"
              color="white"
              borderRadius="50%"
              aria-label={t.voice}
              isDisabled={speech.isMicrophoneAvailable === false}
              onClick={form.dictation.start}
            >
              <Mic size={48} />
            </Button>
          </div>
        )}
      </div>
      {props.showContinue && !!form.promptValue.trim() && (
        <div className={styles.continueAction}>
          <Button
            type="button"
            isDisabled={form.isUploading || speech.listening}
            endIcon={rtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
            onClick={sumbitStep}
          >
            {t.next}
          </Button>
        </div>
      )}
      {form.hasPromptError && (
        <p role="alert" className={styles.error}>
          {t.promptRequired}
        </p>
      )}
      {speech.isMicrophoneAvailable === false && <p role="alert">{t.microphoneDenied}</p>}
    </div>
  )
}
